import {
  isWorkbookErrorCell,
  type WorkbookCell,
  type WorkbookErrorCell,
} from '@erp/shared-types';
import type { ColumnType } from '@erp/workbooks';
import { useGT, useLocale } from 'gt-react';
import {
  cellNumberFormat,
  groupedDecimalText,
  workbookCellText,
} from './workbookCellText';

/**
 * One cell's value as the grids show it: as Excel shows its column's Declared
 * `type` (TRUE/FALSE, short dates), a muted en dash for a null, or an Error
 * cell's error value in red with its cause as the tooltip.
 */
export function WorkbookCellValue({
  cell,
  type,
  percent = false,
  grouped = false,
}: {
  cell: WorkbookCell;
  type?: ColumnType;
  /** Show a Number as a percentage, as a pivot's shown-as Values read. */
  percent?: boolean;
  /** Group a Number's thousands, as a pivot's summarized Values read. */
  grouped?: boolean;
}) {
  const locale = useLocale();
  if (cell === null) return <EmptyValue />;
  if (isWorkbookErrorCell(cell)) return <ErrorValue cell={cell} />;
  const value = Number(cell);
  if (percent && type === 'decimal' && cell !== '' && Number.isFinite(value))
    return cellNumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 2,
    }).format(value);
  if (grouped && type === 'decimal' && typeof cell === 'string')
    return groupedDecimalText(cell, locale);
  return workbookCellText(cell, type, locale);
}

/**
 * A cell's font and alignment: Numbers right-aligned in mono with even digits
 * (DESIGN.md's numeric table values), TRUE/FALSE in mono, dates with even
 * digits, else the UI font.
 */
export function workbookCellFont(type: ColumnType | undefined): string {
  switch (type) {
    case 'decimal':
      return 'font-mono tabular-nums text-right';
    case 'boolean':
      return 'font-mono tabular-nums';
    case 'date':
    case 'datetime':
      return 'tabular-nums';
    default:
      return '';
  }
}

function ErrorValue({ cell }: { cell: WorkbookErrorCell }) {
  const gt = useGT();
  const description = cell.cause
    ? gt('{error}: {cause}', {
        context: 'workbook error cell tooltip: error value and why',
        error: cell.error,
        cause: cell.cause,
      })
    : cell.error;
  return (
    <span
      className="font-medium text-destructive"
      title={description}
      data-error-value={cell.error}
    >
      {cell.error}
      {cell.cause && <span className="sr-only">: {cell.cause}</span>}
    </span>
  );
}

function EmptyValue() {
  const gt = useGT();
  const label = gt('Empty', { context: 'workbook null cell value' });
  return (
    <span className="text-muted-foreground" title={label} data-empty-value="">
      <span aria-hidden="true">–</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/**
 * A cell the shown Result has no value for: a hatched placeholder, so it never
 * reads as an empty value, whose tooltip names the Run that fills it.
 */
export function NeedsRunCell({ hint }: { hint: string }) {
  return (
    // The column header's indicator carries the hint for assistive technology.
    <span
      aria-hidden="true"
      className="block h-4 rounded-xs bg-[repeating-linear-gradient(135deg,var(--color-border)_0_1px,transparent_1px_5px)]"
      title={hint}
      data-needs-run=""
    />
  );
}
